import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaSession } from '../../hooks/useMediaSession';

// How long the controls stay visible after the last user interaction (while playing)
const AUTO_HIDE_MS = 2500;

interface VideoPlayerProps {
  src: string;
  title: string;
  poster?: string;
  className?: string;
  /**
   * Present in the interface for forward compatibility, but the player no longer
   * renders an internal close button. Both call sites (FileExplorer and
   * CutFileExplorer) overlay their own close button outside the player in a
   * `<div className="relative">` wrapper, so they don't pass this prop.
   */
  onClose?: () => void;
}

// Icons
const PlayIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
  </svg>
);

const SkipBackIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
  </svg>
);

const SkipForwardIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
  </svg>
);

const LoopIcon = ({ className = "w-5 h-5", active = false }: { className?: string; active?: boolean }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    {active && <circle cx="12" cy="12" r="2" fill="currentColor" />}
  </svg>
);

const VolumeHighIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const VolumeLowIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
  </svg>
);

const VolumeMuteIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

// Enter fullscreen: two diagonal arrows pointing OUTWARD (top-left + bottom-right)
const EnterFullscreenIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M9 9L4 4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 15v5h-5M15 15l5 5" />
  </svg>
);

// Exit fullscreen: two diagonal arrows pointing INWARD (top-right + bottom-left)
const ExitFullscreenIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 9V4h-5M15 9l5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 15v5h5M9 15l-5 5" />
  </svg>
);

export function VideoPlayer({
  src,
  title,
  poster,
  className = '',
}: Readonly<VideoPlayerProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('video-player-volume');
    return saved ? Number.parseFloat(saved) : 0.7;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canSeek, setCanSeek] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  // Clear all hide-timers on unmount
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // Reset the auto-hide timer. If playing, controls will fade after AUTO_HIDE_MS.
  // If paused, controls stay visible (caller still flips controlsVisible to true).
  const wakeControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (videoRef.current && !videoRef.current.paused) {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), AUTO_HIDE_MS);
    }
  }, []);

  // Keep controls visible whenever playback pauses (don't hide on pause)
  useEffect(() => {
    if (!isPlaying) {
      setControlsVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    }
  }, [isPlaying]);

  // Format time as m:ss
  const formatTime = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Update progress bar background to show played portion
  const updateProgressBackground = useCallback(() => {
    if (progressRef.current && duration > 0) {
      const percent = (currentTime / duration) * 100;
      progressRef.current.style.background = `linear-gradient(to right, hsl(var(--player-progress)) ${percent}%, hsl(var(--player-progress-bg)) ${percent}%)`;
    }
  }, [currentTime, duration]);

  useEffect(() => {
    updateProgressBackground();
  }, [updateProgressBackground]);

  // Video element event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
      if (video.readyState >= 2) {
        setCanSeek(true);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => {
      console.debug('[VideoPlayer] play event', video.currentSrc || video.src);
      setIsPlaying(true);
    };

    const handlePause = () => {
      console.debug('[VideoPlayer] pause event');
      setIsPlaying(false);
    };

    const handleEnded = () => {
      console.debug('[VideoPlayer] ended event');
      if (!isLooping) {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setCanSeek(true);
    };

    const handleWaiting = () => setIsLoading(true);

    const handleStalled = () => {
      console.warn('[VideoPlayer] Video stalled, may need to retry loading');
      setIsLoading(true);
    };

    const handleError = () => {
      console.error('[VideoPlayer] Video error:', video.error);
      setIsLoading(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('error', handleError);

    // Set initial volume and loop
    video.volume = isMuted ? 0 : volume;
    video.loop = isLooping;

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('error', handleError);
    };
  }, [isLooping, isMuted, volume]);

  // Update video volume when volume/mute changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Update loop state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.loop = isLooping;
    }
  }, [isLooping]);

  // Keyboard shortcuts - handled on the wrapping div, NOT globally
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Don't handle if focused on an input element
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

    switch (e.code) {
      case 'Space':
        e.preventDefault();
        togglePlayPause();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        skipBackward();
        break;
      case 'ArrowRight':
        e.preventDefault();
        skipForward();
        break;
      case 'ArrowUp':
        e.preventDefault();
        adjustVolume(0.1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        adjustVolume(-0.1);
        break;
      case 'KeyM':
        e.preventDefault();
        toggleMute();
        break;
      case 'KeyL':
        e.preventDefault();
        toggleLoop();
        break;
      case 'KeyF':
        e.preventDefault();
        toggleFullscreen();
        break;
    }
    // Any key press wakes the overlay controls
    wakeControls();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeControls]);

  // Cleanup: Pause video when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        console.debug('[VideoPlayer] unmount cleanup: pausing and clearing video', videoRef.current.src);
        try {
          videoRef.current.pause();
        } catch (e) {
          console.warn('[VideoPlayer] error pausing video on unmount', e);
        }
        try {
          videoRef.current.currentTime = 0;
        } catch (e) {
          console.warn('[VideoPlayer] error setting currentTime on unmount', e);
        }

        // Clear the src and reload to ensure playback cannot be resumed by stale references
        try {
          videoRef.current.src = '';
          videoRef.current.load();
        } catch (e) {
          console.warn('[VideoPlayer] error clearing video src on unmount', e);
        }

        // Reset Media Session playback state and metadata to avoid OS controls resuming playback
        try {
          if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
            try {
              (navigator as unknown as { mediaSession: { playbackState: string; metadata: null } }).mediaSession.playbackState = 'none';
            } catch (e) {
              console.warn('[VideoPlayer] error setting mediaSession playbackState on unmount', e);
            }
            try {
              (navigator as unknown as { mediaSession: { playbackState: string; metadata: null } }).mediaSession.metadata = null;
            } catch (e) {
              console.warn('[VideoPlayer] error clearing mediaSession metadata on unmount', e);
            }
          }
        } catch (e) {
          console.warn('[VideoPlayer] error resetting mediaSession on unmount', e);
        }
      } else {
        console.debug('[VideoPlayer] unmount cleanup: videoRef.current is null');
      }
    };
  }, []);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch((err) => {
          console.warn('[VideoPlayer] play() rejected:', err);
        });
      } else {
        videoRef.current.pause();
      }
    }
  };

  // Tap on the video: wake the overlay controls (so the user can find them
  // while hidden) but do NOT toggle playback. Use the Play/Pause transport
  // button, Spacebar, or the Media Session API to start/stop playback.
  const handleVideoClick = () => {
    wakeControls();
  };

  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
    }
  };

  const adjustVolume = (delta: number) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVolume);
    localStorage.setItem('video-player-volume', newVolume.toString());
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const toggleLoop = () => {
    setIsLooping(!isLooping);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Sync fullscreen state with browser (covers ESC, browser exit button, iOS exit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement || !!(document as any).webkitFullscreenElement,
      );
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const wrapper = wrapperRef.current;
    const video = videoRef.current;
    if (!video) return;

    const isCurrentlyFullscreen =
      !!document.fullscreenElement || !!(document as any).webkitFullscreenElement;

    if (isCurrentlyFullscreen) {
      const exit = document.exitFullscreen ?? (document as any).webkitExitFullscreen;
      if (exit) exit.call(document);
      return;
    }

    // iOS Safari: webkit fullscreen only works on <video>/<audio> elements.
    // Note: this activates the native iOS video player UI and our overlay
    // controls disappear until the user exits. Documented limitation.
    const videoWithWebkit = video as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
    if (videoWithWebkit.webkitEnterFullscreen) {
      videoWithWebkit.webkitEnterFullscreen();
      return;
    }

    // Desktop + Android Chrome: fullscreen the wrapper so custom controls stay visible
    if (wrapper) {
      const request = wrapper.requestFullscreen ?? (wrapper as any).webkitRequestFullscreen;
      if (request) request.call(wrapper);
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number.parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current && canSeek && !isLoading) {
      try {
        videoRef.current.currentTime = newTime;
      } catch (err) {
        console.warn('[VideoPlayer] Seek failed:', err);
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value);
    setVolume(newVolume);
    localStorage.setItem('video-player-volume', newVolume.toString());
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  // Media Session API integration for lock screen controls
  useMediaSession({
    metadata: {
      title,
      artist: 'BandMate',
    },
    handlers: {
      onPlay: () => {
        console.debug('[VideoPlayer] MediaSession onPlay');
        videoRef.current?.play();
      },
      onPause: () => {
        console.debug('[VideoPlayer] MediaSession onPause');
        videoRef.current?.pause();
      },
      onSeekBackward: skipBackward,
      onSeekForward: skipForward,
    },
    isPlaying,
    positionState: {
      duration,
      position: currentTime,
      playbackRate: 1,
    },
  });

  // Focus inside the wrapper keeps controls visible (keyboard a11y)
  const handleWrapperFocus = useCallback(() => wakeControls(), [wakeControls]);

  let VolumeIcon: (props: { className?: string }) => JSX.Element;
  if (isMuted || volume === 0) {
    VolumeIcon = VolumeMuteIcon;
  } else if (volume < 0.5) {
    VolumeIcon = VolumeLowIcon;
  } else {
    VolumeIcon = VolumeHighIcon;
  }

  // Play/Pause button content extracted to reduce nested ternaries
  let PlayPauseContent: JSX.Element;
  if (isLoading) {
    PlayPauseContent = <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />;
  } else if (isPlaying) {
    PlayPauseContent = <PauseIcon className="w-6 h-6" />;
  } else {
    PlayPauseContent = <PlayIcon className="w-6 h-6" />;
  }

  const fadeTransition = `transition-opacity motion-reduce:transition-none`;

  return (
    <div
      ref={wrapperRef}
      className={`group relative bg-[hsl(var(--player-bg))] ${isFullscreen ? '' : 'border border-border rounded-xl shadow-lg'} overflow-hidden outline-none ${className}`}
      onKeyDown={handleKeyDown}
      onMouseMove={wakeControls}
      onMouseEnter={wakeControls}
      onTouchStart={wakeControls}
      onFocus={handleWrapperFocus}
      tabIndex={0}
      role="group"
      aria-label={`Video player: ${title}`}
    >
      {/* Video element - filling the wrapper, click toggles play & wakes controls */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        className={`block w-full cursor-pointer bg-black object-contain ${isFullscreen ? 'h-full' : 'max-h-[60vh] landscape-short:max-h-[35vh]'}`}
        onClick={handleVideoClick}
      />

      {/* Top overlay - title (visible only when controls are visible) */}
      <div
        className={`absolute inset-x-0 top-0 pointer-events-none bg-gradient-to-b from-black/70 to-transparent p-3 md:p-4 ${fadeTransition} duration-200 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden={!controlsVisible}
      >
        <h3 className="text-white font-semibold text-sm md:text-base truncate drop-shadow">
          {title}
        </h3>
      </div>

      {/* Bottom overlay - controls (visible only when controls are visible) */}
      <div
        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 md:p-4 space-y-2 ${fadeTransition} duration-200 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!controlsVisible}
      >
        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-white/70 w-10 text-right tabular-nums">
            {formatTime(currentTime)}
          </span>
          <input
            ref={progressRef}
            type="range"
            min="0"
            max={duration || 100}
            step="0.1"
            value={currentTime}
            onChange={handleProgressChange}
            onFocus={wakeControls}
            disabled={!canSeek}
            className={`flex-1 h-1 player-progress ${!canSeek ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label="Seek"
            title={!canSeek ? 'Video not ready for seeking' : 'Seek'}
          />
          <span className="text-xs text-white/70 w-10 tabular-nums">
            {formatTime(duration)}
          </span>
        </div>

        {/* Transport row - flex-1 outer groups so the center cluster is truly centered */}
        <div className="flex items-center gap-2">
          {/* Left group - Loop, takes equal space so center stays centered */}
          <div className="flex-1 flex items-center justify-start">
            <button
              onClick={toggleLoop}
              className={`p-2 rounded-full transition-colors ${
                isLooping
                  ? 'text-[hsl(var(--player-button))] bg-white/10'
                  : 'text-white/70 hover:text-white'
              }`}
              aria-label={isLooping ? 'Disable loop' : 'Enable loop'}
              title="Loop (L)"
            >
              <LoopIcon className="w-5 h-5" active={isLooping} />
            </button>
          </div>

          {/* Center group - skip-back, play, skip-fwd */}
          <div className="flex items-center gap-1">
            <button
              onClick={skipBackward}
              className="p-2 md:p-2.5 text-white/70 hover:text-white transition-colors rounded-full"
              aria-label="Skip back 10 seconds"
              title="Skip back 10s (←)"
            >
              <SkipBackIcon className="w-6 h-6" />
            </button>

            <button
              onClick={() => { togglePlayPause(); wakeControls(); }}
              disabled={isLoading}
              className="p-3 md:p-3.5 bg-[hsl(var(--player-button))] hover:bg-[hsl(var(--player-button-hover))] text-white rounded-full transition-colors disabled:opacity-50"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {PlayPauseContent}
            </button>

            <button
              onClick={skipForward}
              className="p-2 md:p-2.5 text-white/70 hover:text-white transition-colors rounded-full"
              aria-label="Skip forward 10 seconds"
              title="Skip forward 10s (→)"
            >
              <SkipForwardIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Right group - Volume + Fullscreen, takes equal space */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <button
              onClick={toggleMute}
              className="p-2 text-white/70 hover:text-white transition-colors rounded-full"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              title="Mute (M)"
            >
              <VolumeIcon className="w-5 h-5" />
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              onFocus={wakeControls}
              className="w-16 md:w-20 player-volume hidden sm:block"
              aria-label="Volume"
            />
            <button
              onClick={toggleFullscreen}
              className="p-2 text-white hover:text-[hsl(var(--player-button))] transition-colors rounded-full"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={
                isFullscreen
                  ? 'Exit fullscreen (F / Esc)'
                  : 'Fullscreen (F) — iOS opens the native player'
              }
            >
              {isFullscreen ? <ExitFullscreenIcon className="w-6 h-6" /> : <EnterFullscreenIcon className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Keyboard hint - hidden on small screens where controls are touched, not keyed */}
        <div className="text-center hidden sm:block pt-1">
          <span className="text-xs text-white/60">
            Space: play/pause · ←/→: seek · ↑/↓: volume · M: mute · L: loop · F: fullscreen
          </span>
        </div>
      </div>
    </div>
  );
}

VideoPlayer.displayName = 'VideoPlayer';
